import {useAuth0} from "@auth0/auth0-react";
import {TalentApplication, TalentApplicationStatusUpdate} from "../model/talent_application";
import {FileUploadRequest, UploadedFile} from "./file_api";
import {TrainingAnalysisResponse} from "./training_analysis_api";

export function useTalentApplicationAPI() {
    const { getAccessTokenSilently } = useAuth0();

    const findApplications = (params: { q: string; limit: number; offset: number; status?: string}): Promise<TalentApplication[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                const query = new URLSearchParams({
                    q: params.q,
                    limit: params.limit.toString(),
                    offset: params.offset.toString(),
                });
                query.append("status", params.status || "");
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/find?${query.toString()}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: TalentApplication[]) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch talent applications: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const getApplication = (id: string): Promise<TalentApplication> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/${id}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: TalentApplication) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch talent application: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const updateApplicationStatus = (id: string, updateData: TalentApplicationStatusUpdate & { comment?: string }): Promise<TalentApplication> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                const queryParams = new URLSearchParams();
                queryParams.append("status", updateData.status);
                if (updateData.comment) {
                    queryParams.append("comment", updateData.comment);
                }
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/status/${id}?${queryParams.toString()}`, {
                    method: "PUT",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            response.json().then((data: TalentApplication) => resolve(data)).catch(reject);
                        } else {
                            response.json().then((error) => {
                                if (error.message) {
                                    reject(new Error(error.message))
                                } else {
                                    reject(new Error(JSON.stringify(error)));
                                }
                            })
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const deleteApplication = (id: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/${id}`, {
                    method: "DELETE",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            resolve();
                        } else {
                            response.json().then(error => {
                                if (error.message) {
                                    reject(new Error(error.message))
                                } else {
                                    reject(new Error(JSON.stringify(error)));
                                }
                            })
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };
    const removeTrainingFile = (applicationId: string, fileId: string): Promise<TalentApplication> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/${applicationId}/training/${fileId}`, {
                    method: "DELETE",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json()
                                .then((data: TalentApplication) => resolve(data))
                                .catch(reject);
                        } else {
                            response.json().then(error => {
                                if (error.message) {
                                    reject(new Error(error.message))
                                } else {
                                    reject(new Error(JSON.stringify(error)));
                                }
                            })
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }
    const resendWelcomeEmail = (applicationId: string): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/${applicationId}/welcome-email`, {
                    method: "POST",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.ok) {
                            resolve(true)
                        } else {
                            response.json().then(error => {
                                if (error.message) {
                                    reject(new Error(error.message))
                                } else {
                                    reject(new Error(JSON.stringify(error)));
                                }
                            });
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }

    const uploadFile = async (
      uploadRequest: FileUploadRequest,
      authCode: string,
      onUploadProgress: (progress: { loaded: number; total: number }) => void
  ): Promise<UploadedFile[]> => {

    return new Promise<UploadedFile[]>((resolve, reject) => {
      const params = new URLSearchParams();
      params.append("is_public", uploadRequest.is_public.toString());
      params.append("object_type", uploadRequest.object_type);
      if (uploadRequest.object_id) {
        params.append("object_id", uploadRequest.object_id);
      }
      if (uploadRequest.generate_thumbnail) {
        params.append("generate_thumbnail", uploadRequest.generate_thumbnail.toString());
      }
      if (uploadRequest.resize) {
        params.append("resize", uploadRequest.resize);
      }
      if (uploadRequest.usage) {
        params.append("usage", uploadRequest.usage);
      }
      params.append("auth_code", authCode);
      const formData = new FormData();
      uploadRequest.files.forEach((file) => {
        formData.append("files", file);
      });
      //Need to use axios instead of fetch to support progress events
      getAccessTokenSilently().then((accessToken) => {
        fetch(`${process.env.REACT_APP_API_URL}/talent-application/public/upload?${params.toString()}`, {
          method: 'POST',
          headers: {
            'authorization': `Bearer ${accessToken}`,
          },
          body: formData
        })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error("Failed to upload");
          }
        })
        .then((data) => {
          resolve(data);
        })
        .catch(reject);
      }).catch(reject);
    });
  }

    const getAnalysisStatus = (applicationId: string): Promise<TrainingAnalysisResponse | null> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/talent-application/admin/${applicationId}/analysis`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: TrainingAnalysisResponse) => resolve(data)).catch(reject);
                        } else if (response.status === 404) {
                            // No analysis available yet
                            resolve(null);
                        } else {
                            reject(new Error("Failed to fetch training analysis: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    return {
        findApplications,
        getApplication,
        updateApplicationStatus,
        deleteApplication,
        removeTrainingFile,
        resendWelcomeEmail,
        uploadFile,
        getAnalysisStatus
    };
}
